import { T } from '@territory/tokens';

/*
 * Shared by AttentionCard and QueueDrawer: the severity hues, the wash behind hovered rows, and the one sort rule
 * (red before amber, then oldest first). Colors are T tokens only; tints are color-mix over them.
 */
export type Severity = 'red' | 'amber';

/* Text. amberInk and red clear 4.5:1 on the card surface in light and dark. */
export const severityInk: Record<Severity, string> = { red: T.red, amber: T.amberInk };
/* Dots, edges and bars. */
export const severityLine: Record<Severity, string> = { red: T.red, amber: T.amber };

export const mix = (color: string, pct: number): string => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

/* The reference --track: a faint wash that reads on a light surface and on the dark green one. */
export const WASH = mix(T.label, 6);

/* Milliseconds for an ISO timestamp; missing or unparseable sorts after every real time. */
export const timeOf = (iso: string | null | undefined): number => {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

const cmp = (a: number, b: number): number => (a === b ? 0 : a < b ? -1 : 1);

/* Red before amber, then ageAt ascending (oldest first). Stable: equal rows keep their input order. */
export function sortBySeverityThenAge<R extends { severity: Severity; ageAt: string | null }>(rows: readonly R[]): R[] {
  return rows
    .map((row, i) => ({ row, i }))
    .sort(
      (a, b) =>
        cmp(a.row.severity === 'red' ? 0 : 1, b.row.severity === 'red' ? 0 : 1) ||
        cmp(timeOf(a.row.ageAt), timeOf(b.row.ageAt)) ||
        a.i - b.i,
    )
    .map((x) => x.row);
}

/* "5m", "3h", "12d" for the age of an ISO timestamp. Null or unparseable is a hyphen. */
export function formatAge(iso: string | null | undefined, now: number = Date.now()): string {
  const t = timeOf(iso);
  if (!Number.isFinite(t)) return '-';
  const mins = Math.max(0, Math.floor((now - t) / 60000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
